import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { CalendarCheck, Users, MessageSquare, Clock, Check, XCircle, AlertCircle, Download } from 'lucide-react';

const PINK = '#FFCCE0';
const PINK_DARK = '#F5B8D0';
const PINK_ACCENT = '#e891b9';
const GREEN = '#22c55e';
const YELLOW = '#eab308';
const RED = '#ef4444';
const GRAY = '#6b7280';

const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const DashboardTab = ({ reservations = [], messages = [] }) => {
  const stats = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];

    // Monthly reservations
    const monthRes = reservations.filter(r => r.date >= monthStart);
    const confirmed = monthRes.filter(r => r.status === 'confirmed').length;
    const pending = monthRes.filter(r => r.status === 'pending').length;
    const cancelled = monthRes.filter(r => r.status === 'cancelled').length;

    // Today's confirmed
    const todayRes = reservations.filter(r => r.date === todayStr && r.status === 'confirmed');
    const todayGuests = todayRes.reduce((sum, r) => sum + (r.guests || 0), 0);

    // Unread messages
    const unread = messages.filter(m => !m.read).length;

    // By day of week (from all reservations)
    const byDay = [0, 0, 0, 0, 0, 0, 0];
    reservations.forEach(r => {
      if (r.date) {
        const d = new Date(r.date + 'T12:00:00');
        const day = d.getDay();
        // Convert Sunday=0 to index 6, Monday=1 to index 0
        byDay[day === 0 ? 6 : day - 1]++;
      }
    });
    const dayData = DAY_NAMES.map((name, i) => ({ name, value: byDay[i] }));

    // By hour (from all reservations)
    const byHour = {};
    reservations.forEach(r => {
      if (r.time) {
        const hour = r.time.split(':')[0];
        const label = `${hour}:00`;
        byHour[label] = (byHour[label] || 0) + 1;
      }
    });
    const hourData = Object.entries(byHour)
      .map(([name, value]) => ({ name, value, sortKey: parseInt(name) }))
      .sort((a, b) => a.sortKey - b.sortKey);

    // Sort today's reservations by time
    const todaySorted = [...todayRes].sort((a, b) => (a.time || '').localeCompare(b.time || ''));

    return {
      total: monthRes.length, confirmed, pending, cancelled,
      todayRes: todaySorted, todayGuests,
      unread, totalMessages: messages.length,
      dayData, hourData,
    };
  }, [reservations, messages]);

  const handleExport = async (type) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = sessionStorage.getItem('admin_token');
    try {
      const res = await fetch(`${API_URL}/api/admin/export/${type}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'reservations' ? 'reservaciones_lavieenrose.csv' : 'mensajes_lavieenrose.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error:', e);
    }
  };

  const StatCard = ({ icon: Icon, label, value, sub, color }) => (
    <Card className="border-0 shadow-sm" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">{label}</p>
            <p className="text-3xl font-bold" style={{ color: color || '#1f2937' }}>{value}</p>
            {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: PINK }}>
            <Icon className="w-5 h-5 text-gray-700" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
          <p className="font-medium">{label}</p>
          <p className="text-gray-600">{payload[0].value} reservaciones</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label="Este mes" value={stats.total}
          sub={`${stats.confirmed} confirmadas`} />
        <StatCard icon={Clock} label="Pendientes" value={stats.pending}
          sub="Por confirmar" color={stats.pending > 0 ? YELLOW : undefined} />
        <StatCard icon={Users} label="Hoy" value={stats.todayGuests}
          sub={`${stats.todayRes.length} reservaciones`} color={GREEN} />
        <StatCard icon={MessageSquare} label="Mensajes" value={stats.unread}
          sub={`${stats.totalMessages} total`} color={stats.unread > 0 ? RED : undefined} />
      </div>

      {/* Export Buttons */}
      <div className="flex gap-3">
        <Button variant="outline" size="sm" onClick={() => handleExport('reservations')} data-testid="export-reservations-btn">
          <Download className="w-4 h-4 mr-2" />
          Exportar Reservaciones (CSV)
        </Button>
        <Button variant="outline" size="sm" onClick={() => handleExport('messages')} data-testid="export-messages-btn">
          <Download className="w-4 h-4 mr-2" />
          Exportar Mensajes (CSV)
        </Button>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Reservaciones por día</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.dayData.some(d => d.value > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.dayData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {stats.dayData.map((entry, i) => (
                      <Cell key={i} fill={i >= 5 ? PINK_ACCENT : PINK_DARK} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
                Sin datos de reservaciones
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Horarios populares</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.hourData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.hourData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill={PINK_ACCENT} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
                Sin datos de horarios
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Today's Reservations */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Reservaciones de hoy</CardTitle>
        </CardHeader>
        <CardContent>
          {stats.todayRes.length > 0 ? (
            <div className="divide-y">
              {stats.todayRes.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-gray-700" style={{ backgroundColor: PINK }}>
                      {(r.name || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{r.name}</p>
                      <p className="text-xs text-gray-500">{r.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="font-mono font-medium">{r.time}</span>
                    <Badge variant="outline" className="text-xs">
                      <Users className="w-3 h-3 mr-1" />{r.guests}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-400 text-sm">
              <CalendarCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No hay reservaciones confirmadas para hoy
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Breakdown */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Resumen del mes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#f0fdf4' }}>
              <Check className="w-5 h-5 mx-auto mb-1 text-green-600" />
              <p className="text-2xl font-bold text-green-700">{stats.confirmed}</p>
              <p className="text-xs text-green-600">Confirmadas</p>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#fefce8' }}>
              <AlertCircle className="w-5 h-5 mx-auto mb-1 text-yellow-600" />
              <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
              <p className="text-xs text-yellow-600">Pendientes</p>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#fef2f2' }}>
              <XCircle className="w-5 h-5 mx-auto mb-1 text-red-600" />
              <p className="text-2xl font-bold text-red-700">{stats.cancelled}</p>
              <p className="text-xs text-red-600">Canceladas</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardTab;
